// groupNearbyPhotoItems.js

import { ACTIVITY_FEED_ITEM_KIND } from 'node/activity/utils/buildActivityFeedItems';
import { getActivityItemTimestampMs } from 'node/activity/utils/groupActivityItemsByDay';

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeTypeKey(value) {
  return String(value || '').trim().toUpperCase();
}

export const HOME_ACTIVITY_PHOTO_GROUP_KIND = 'photo-group';
export const HOME_PHOTO_GROUP_WINDOW_MS = 5 * 60 * 1000;

/**
 * Actor identity for photo-add clustering.
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function getPhotoAddActorKey(item) {
  const event = item?.event || {};
  return String(
    event.actorKey
    || event.actorUserKey
    || event.userKey
    || event.user?.key
    || event.user?.userKey
    || event.actorName
    || event.user?.title
    || event.user?.name
    || ''
  ).trim().toLowerCase();
}

/**
 * True when a Home row is a photo-linked relationship add.
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function isHomePhotoAddItem(item) {
  if (item?.kind !== ACTIVITY_FEED_ITEM_KIND.SYSTEM) return false;
  const event = item.event;
  if (!event) return false;

  const isRelationship = event.kind === 'relationship-changed'
    || event.type === 'node_relationship_changed';
  if (!isRelationship) return false;

  const change = Array.isArray(event.changes) ? event.changes[0] : null;
  if (String(change?.changeKind || '').trim() === 'edge_unlinked') return false;

  const typeKey = normalizeTypeKey(
    change?.relatedNodeType || event.relatedNode?.type
  );
  if (typeKey === 'PHOTO') return true;

  const typeLabel = String(
    change?.relatedNodeTypeLabel || event.relatedNodeTypeLabel || ''
  ).trim().toLowerCase();
  return typeLabel === 'photo';
}

/**
 * Copy after the actor name for a clustered photo add.
 *
 * @param {number} count
 * @returns {string}
 */
export function formatPhotoGroupMessage(count) {
  const total = Number(count);
  if (!Number.isFinite(total) || total <= 1) return 'added a photo.';
  return `added ${total} photos.`;
}

/**
 * Collapse consecutive same-actor photo adds that fall inside a 5-minute
 * window from the first item. Display only — events stay separate.
 *
 * @param {object[]} items
 * @param {{ windowMs?: number }} [options]
 * @returns {object[]}
 */
export function groupNearbyPhotoItems(items, {
  windowMs = HOME_PHOTO_GROUP_WINDOW_MS,
} = {}) {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  const rows = [];
  let index = 0;

  while (index < list.length) {
    const item = list[index];
    if (!isHomePhotoAddItem(item)) {
      rows.push(item);
      index += 1;
      continue;
    }

    const startMs = getActivityItemTimestampMs(item);
    const actorKey = getPhotoAddActorKey(item);
    const run = [item];
    let cursor = index + 1;

    while (cursor < list.length) {
      const candidate = list[cursor];
      if (!isHomePhotoAddItem(candidate)) break;

      const candidateActor = getPhotoAddActorKey(candidate);
      if (actorKey && candidateActor && candidateActor !== actorKey) break;

      const candidateMs = getActivityItemTimestampMs(candidate);
      if (Math.abs(candidateMs - startMs) > windowMs) break;

      run.push(candidate);
      cursor += 1;
    }

    if (run.length === 1) {
      rows.push(item);
    } else {
      rows.push({
        kind: HOME_ACTIVITY_PHOTO_GROUP_KIND,
        key: `photo-group:${run.map((row) => row.key).join('+')}`,
        at: startMs,
        event: item.event,
        node: item.node || null,
        reactions: item.reactions || [],
        items: run,
      });
    }

    index = cursor;
  }

  return rows;
}
